import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import Colors from "../../../assets/Variables/ColorsVariables.js";
import * as TextVariables from "../../../assets/Variables/TextVariables.js";
import NotificationModal from "../../../components/Notifications/NotificationModal.js";
import SteeperComponent from "../../../components/Steeper/SteeperComponent.js";
import CreateMAMRequest from "../../../Endpoints/CreateMAMRequest.js";

const steps = ["IB / Agent", "Sub Accounts", "Configurations"];

function CreateMAMStepThreePage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [activeStep] = useState(3);
  const [showNotification, setShowNotification] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });

  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formValues, setFormValues] = useState({
    platform: "",
    accountType: "",
    leverage: "",
    pips: "",
    allocation: "",
    pfPercent: "",
    comment: "",
  });

  useEffect(() => {
    const hasStep1 = state?.accountEmail || state?.ibId;
    const hasStep2 =
      Array.isArray(state?.subAccounts) && state?.subAccounts.length > 0;

    if (!hasStep1) {
      navigate("/requests/create-mam-account");
      return;
    }

    if (!hasStep2) {
      navigate("/requests/create-mam-account/step-two", { state });
    }
  }, [state, navigate]);

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleOnAcceptSuccess = () => {
    setShowNotification(false);
    navigate("/requests");
  };

  const handleOnAcceptRetry = () => {
    setShowNotification(false);
  };

  const handleOnRefuse = () => {
    setShowNotification(false);
  };

  const showWarn = (title, message) => {
    setModalProps({
      iconType: "warning",
      title,
      message,
      onAcceptLabel: "Close",
      onRefuseLabel: undefined,
      onAccept: handleOnAcceptRetry,
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  const commentCount = formValues.comment.length;

  const errors = useMemo(() => {
    const e = {
      platform: !formValues.platform,
      accountType: !formValues.accountType,
      leverage: !formValues.leverage,
      pips: false,
      allocation: !formValues.allocation,
      pfPercent: false,
    };

    if (String(formValues.pips).trim()) {
      const n = Number(formValues.pips);
      if (!Number.isFinite(n) || n < 0) e.pips = true;
    }

    if (String(formValues.pfPercent).trim()) {
      const n = Number(formValues.pfPercent);
      if (!Number.isFinite(n) || n < 0 || n > 100) e.pfPercent = true;
    }

    return e;
  }, [formValues]);

  const canContinue =
    !errors.platform &&
    !errors.accountType &&
    !errors.leverage &&
    !errors.pips &&
    !errors.allocation &&
    !errors.pfPercent &&
    !submitting;

  const handleBack = () => {
    navigate("/requests/create-mam-account/step-two", { state });
  };

  const handleContinue = async () => {
    setSubmittedOnce(true);

    if (
      errors.platform ||
      errors.accountType ||
      errors.leverage ||
      errors.pips ||
      errors.allocation ||
      errors.pfPercent
    ) {
      showWarn("Incomplete fields", "Please fill in the missing fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        RequestType: "BROKER_MAM_ACCOUNT_CREATE",
        IBEmail: state?.accountEmail?.trim() || "",
        IBId: state?.ibId?.trim() || "",
        IBPlatform: state?.ibPlatform?.trim() || "",
        SubAccounts: Array.isArray(state?.subAccounts) ? state.subAccounts : [],
        MAMPlatform: formValues.platform?.trim() || "",
        AccountType: formValues.accountType?.trim() || "",
        Leverage: formValues.leverage?.trim() || "",
        Pips: formValues.pips?.trim() || "",
        Allocation: formValues.allocation?.trim() || "",
        PF: formValues.pfPercent?.trim() || "",
        Comment: formValues.comment?.trim() || "",
      };

      const response = await CreateMAMRequest.create(payload);

      if (response && response.data != null) {
        setModalProps({
          iconType: "success",
          title: "Your MAM account creation request was sent successfully",
          message: (
            <>
              Our team will review your request and notify you of the resolution
              shortly.
              <br />
              <br />
              You can also check the status of your request in the{" "}
              <strong>Requests</strong> tab.
            </>
          ),
          onAcceptLabel: "Continue",
          onRefuseLabel: undefined,
          onAccept: handleOnAcceptSuccess,
          onRefuse: undefined,
        });
        setShowNotification(true);
      } else {
        setModalProps({
          iconType: "warning",
          title: "Error in request",
          message: "Please try later",
          onAcceptLabel: "Try again",
          onRefuseLabel: "Close",
          onAccept: handleOnAcceptRetry,
          onRefuse: handleOnRefuse,
        });
        setShowNotification(true);
      }
    } catch (err) {
      console.error(err);

      if (err.response) {
        if (err.response.status === 400) {
          setModalProps({
            iconType: "warning",
            title: "Problem with request",
            message: err.response?.data?.message || "Please verify the information and try again.",
            onAcceptLabel: "Try again",
            onRefuseLabel: undefined,
            onAccept: handleOnAcceptRetry,
            onRefuse: undefined,
          });
        } else {
          setModalProps({
            iconType: "error",
            title: "Problem with service",
            message:
              "The following error has been caused: " +
              (err.response?.data?.message || "Unknown error") +
              ". Please contact an administrator",
            onAcceptLabel: "Accept",
            onRefuseLabel: undefined,
            onAccept: handleOnAcceptRetry,
            onRefuse: undefined,
          });
        }
      } else {
        setModalProps({
          iconType: "error",
          title: "Problem with service",
          message: "Please contact an administrator - " + err.message,
          onAcceptLabel: "Accept",
          onRefuseLabel: undefined,
          onAccept: handleOnAcceptRetry,
          onRefuse: undefined,
        });
      }

      setShowNotification(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 4,
        maxWidth: "calc(100vw - 280px)",
        minHeight: "calc(100vh - 100px)",
        marginTop: "0px",
        marginLeft: "10px",
        borderRadius: "12px",
        backgroundColor: "#F9F9F9",
      }}
    >
      <NotificationModal
        open={showNotification}
        onClose={handleCloseNotification}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <Button
          onClick={() => navigate("/requests")}
          startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
          sx={{ textTransform: "none", color: "black", fontWeight: "bold" }}
        >
          Go back
        </Button>
      </Box>

      <Box sx={{ width: "70%", margin: "0 auto", marginTop: "6px" }}>
        <SteeperComponent stepList={steps} stepNumber={activeStep} />
      </Box>

      <Paper
        elevation={2}
        sx={{
          padding: 4,
          maxWidth: "70%",
          margin: "18px auto 0 auto",
          borderRadius: "12px",
          minHeight: "330px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography sx={{ fontFamily: TextVariables.Body_3_Bold, marginBottom: "16px" }}>
          Step 3: Configurations
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          <FormControl fullWidth error={submittedOnce && errors.platform}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              Platform
            </Typography>
            <Select
              value={formValues.platform}
              onChange={(e) =>
                setFormValues((p) => ({ ...p, platform: e.target.value }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
            >
              <MenuItem value="">
                <em>Select where the MAM will be created</em>
              </MenuItem>
              <MenuItem value="MT4 LTD LIVE 1">MT4 LTD LIVE 1</MenuItem>
              <MenuItem value="MT4 LTD LIVE 2">MT4 LTD LIVE 2</MenuItem>
              <MenuItem value="MT5 LTD LIVE 1">MT5 LTD LIVE 1</MenuItem>
            </Select>
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.platform ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.platform ? "Please, select a platform" : "."}
            </Typography>
          </FormControl>

          <FormControl fullWidth error={submittedOnce && errors.accountType}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              Account Type
            </Typography>
            <Select
              value={formValues.accountType}
              onChange={(e) =>
                setFormValues((p) => ({ ...p, accountType: e.target.value }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
            >
              <MenuItem value="">
                <em>Select the type of the MAM</em>
              </MenuItem>
              <MenuItem value="iLC">iLC</MenuItem>
              <MenuItem value="XLev">XLev</MenuItem>
            </Select>
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.accountType ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.accountType
                ? "Please, select an account type"
                : "."}
            </Typography>
          </FormControl>

          <FormControl fullWidth error={submittedOnce && errors.leverage}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              Leverage
            </Typography>
            <Select
              value={formValues.leverage}
              onChange={(e) =>
                setFormValues((p) => ({ ...p, leverage: e.target.value }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
            >
              <MenuItem value="">
                <em>Select the leverage of the list</em>
              </MenuItem>
              <MenuItem value="1:1">1:1</MenuItem>
              <MenuItem value="1:2">1:2</MenuItem>
              <MenuItem value="1:3">1:3</MenuItem>
              <MenuItem value="1:5">1:5</MenuItem>
              <MenuItem value="1:10">1:10</MenuItem>
              <MenuItem value="1:15">1:15</MenuItem>
              <MenuItem value="1:20">1:20</MenuItem>
              <MenuItem value="1:25">1:25</MenuItem>
              <MenuItem value="1:33">1:33</MenuItem>
              <MenuItem value="1:40">1:40</MenuItem>
              <MenuItem value="1:50">1:50</MenuItem>
              <MenuItem value="1:66">1:66</MenuItem>
              <MenuItem value="1:75">1:75</MenuItem>
              <MenuItem value="1:80">1:80</MenuItem>
              <MenuItem value="1:100">1:100</MenuItem>
              <MenuItem value="1:125">1:125</MenuItem>
              <MenuItem value="1:150">1:150</MenuItem>
              <MenuItem value="1:175">1:175</MenuItem>
              <MenuItem value="1:200">1:200</MenuItem>
              <MenuItem value="1:300">1:300</MenuItem>
              <MenuItem value="1:400">1:400</MenuItem>
              <MenuItem value="1:500">1:500</MenuItem>
              <MenuItem value="1:1000">1:1000</MenuItem>
              <MenuItem value="1:5000">1:5000</MenuItem>
            </Select>
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.leverage ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.leverage ? "Please, select a leverage" : "."}
            </Typography>
          </FormControl>

          <FormControl fullWidth error={submittedOnce && errors.pips}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              PIPs
            </Typography>
            <OutlinedInput
              value={formValues.pips}
              onChange={(e) =>
                setFormValues((p) => ({
                  ...p,
                  pips: e.target.value.replace(/[^\d.]/g, "").slice(0, 12),
                }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
              placeholder="Enter a pips value"
            />
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.pips ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.pips ? "Please, enter a valid pips number" : "."}
            </Typography>
          </FormControl>

          <FormControl fullWidth error={submittedOnce && errors.allocation}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              Allocation
            </Typography>
            <Select
              value={formValues.allocation}
              onChange={(e) =>
                setFormValues((p) => ({ ...p, allocation: e.target.value }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
            >
              <MenuItem value="">
                <em>Select the MAM allocation option</em>
              </MenuItem>
              <MenuItem value="MAM Cash Equity">MAM Cash Equity</MenuItem>
              <MenuItem value="MAM Equity">MAM Equity</MenuItem>
            </Select>
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.allocation ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.allocation
                ? "Please, select an allocation option"
                : "."}
            </Typography>
          </FormControl>

          <FormControl fullWidth error={submittedOnce && errors.pfPercent}>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              PF %
            </Typography>
            <OutlinedInput
              value={formValues.pfPercent}
              onChange={(e) =>
                setFormValues((p) => ({
                  ...p,
                  pfPercent: e.target.value.replace(/[^\d.]/g, "").slice(0, 6),
                }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
              placeholder="Enter a PF value in percentage"
            />
            <Typography
              sx={{
                fontFamily: TextVariables.Caption_1,
                color: submittedOnce && errors.pfPercent ? "#d32f2f" : "transparent",
                mt: "4px",
              }}
            >
              {submittedOnce && errors.pfPercent
                ? "Please, enter a value between 0 and 100"
                : "."}
            </Typography>
          </FormControl>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <FormControl fullWidth>
            <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
              Comment
            </Typography>
            <OutlinedInput
              value={formValues.comment}
              onChange={(e) =>
                setFormValues((p) => ({
                  ...p,
                  comment: e.target.value.slice(0, 200),
                }))
              }
              sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
              placeholder="Enter any additional details for the MAM creation"
              multiline
              minRows={2}
            />
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Typography sx={{ fontFamily: TextVariables.Caption_1, color: "#6B7280" }}>
              {commentCount}/200
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              paddingX: 3,
              paddingY: 1.1,
              borderColor: "#111827",
              color: "#111827",
              "&:hover": { borderColor: "#111827" },
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={submitting}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 800,
              paddingX: 3,
              paddingY: 1.1,
              backgroundColor: submitting ? "#E5E7EB" : Colors.Primary_600,
              color: submitting ? "#6B7280" : "#FFF",
              "&:hover": {
                backgroundColor: submitting ? "#E5E7EB" : Colors.Primary_700,
              },
              "&:disabled": { color: "#6B7280" },
            }}
          >
            {submitting ? "Submitting..." : "Continue"}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ marginBottom: "100px" }} />
    </Paper>
  );
}

export default CreateMAMStepThreePage;